import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { apiPost } from "../../services/api/apiClient";
import type { TokenResponse } from "../../types/api";

interface LoginState {
  user: TokenResponse | null;
  userIsValid: boolean;
  needValidation: boolean;
  validatingUser: boolean;
  loading: boolean;
  resetPasswordSuccess: boolean;
  resetPasswordError: string | null;
  resetTokenSuccess: boolean;
  resetTokenError: string | null;
  changePasswordSuccess: boolean;
  changePasswordError: string | null;
  setPasswordSuccess: boolean;
  setPasswordError: string | null;
  loginError: string | null;
  loginSuccess: boolean;
  signUpError: string | null;
  signUpSuccess: boolean;
  confirmEmailSuccess: boolean;
  confirmEmailError: string | null;
  verifyMagicLinkError: string | null;
  verifyMagicLinkSuccess: boolean;
  magicLinkSent: boolean;
  magicLinkError: string | null;
}

// Initial state
const initialState: LoginState = {
  user: JSON.parse(localStorage.getItem("user") || "null") || null,

  userIsValid: false,
  needValidation: true,
  validatingUser: false,
  loading: false,
  resetPasswordSuccess: false,
  resetPasswordError: null,

  resetTokenSuccess: false,
  resetTokenError: null,

  changePasswordSuccess: false,
  changePasswordError: null,

  setPasswordSuccess: false,
  setPasswordError: null,

  loginError: null,
  loginSuccess: false,

  signUpError: null,
  signUpSuccess: false,

  confirmEmailSuccess: false,
  confirmEmailError: null,

  verifyMagicLinkError: null,
  verifyMagicLinkSuccess: false,

  magicLinkSent: false,
  magicLinkError: null,
};

// Helper function to create Basic Auth header
const createBasicAuthHeader = (username: any, password: any) => {
  const credentials = btoa(`${username}:${password}`);
  return `Basic ${credentials}`;
};

// Async thunk for email/password login
export const loginWithEmail = createAsyncThunk(
  "login/loginWithEmail",
  async ({ email, password, remember }: any, { rejectWithValue }) => {
    try {
      const user = await apiPost({
        endpoint: "/login",
        data: { remember },
        headers: {
          Authorization: createBasicAuthHeader(email, password),
        },
      });
      localStorage.setItem("user", JSON.stringify(user));
      return user;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);

// Async thunk for secure link login
export const loginWithMagicLink = createAsyncThunk(
  "login/loginWithMagicLink",
  async ({ email, remember }: any, { rejectWithValue }) => {
    try {
      await apiPost({
        endpoint: "/send-secure-link",
        data: { email, remember },
      });
      return;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);

// Async thunk for verifying secure link
export const verifyMagicLink = createAsyncThunk(
  "login/verifyMagicLink",
  async ({ token }: any, { rejectWithValue }) => {
    try {
      const user = await apiPost({
        endpoint: "/verify-secure-link",
        data: { token },
      });
      localStorage.setItem("user", JSON.stringify(user));
      return user;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);

// Async thunk for reset password
export const requestResetToken = createAsyncThunk(
  "login/requestResetToken",
  async ({ email }: any, { rejectWithValue }) => {
    try {
      const result = await apiPost({
        endpoint: "/request-reset-token",
        data: { email },
      });
      return result;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);

// Async thunk for reset password
export const resetPassword = createAsyncThunk(
  "login/resetPassword",
  async ({ email, resetToken, newPassword }: any, { rejectWithValue }) => {
    try {
      const result = await apiPost({
        endpoint: "/reset-password",
        data: { email, resetToken, newPassword },
      });
      return result;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);

// Async thunk for changing password in dashboard
export const changePassword = createAsyncThunk(
  "login/changePassword",
  async ({ currentPassword, newPassword }: any, { rejectWithValue, getState }) => {
    try {
      const { login } = getState() as any;
      const user = login.user;

      const result = await apiPost({
        endpoint: "/change-password",
        data: { currentPassword, newPassword },
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      });
      return result;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);

// Async thunk for setting password for the first time
export const setPassword = createAsyncThunk(
  "login/setPassword",
  async ({ newPassword }: any, { rejectWithValue, getState }) => {
    try {
      const { login } = getState() as any;
      const user = login.user;

      const result = await apiPost({
        endpoint: "/set-password",
        data: { newPassword },
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      });
      return result;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);

// Async thunk for signup (public self-registration)
export const signup = createAsyncThunk(
  "login/signup",
  async (payload: any, { rejectWithValue }) => {
    try {
      const user = await apiPost({
        endpoint: "/public/register",
        data: payload,
      });
      localStorage.setItem("user", JSON.stringify(user));
      return user;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);

// Async thunk for confirming email
export const confirmEmail = createAsyncThunk(
  "login/confirmEmail",
  async ({ email, token }: any, { rejectWithValue }) => {
    try {
      const result = await apiPost({
        endpoint: "/confirm-email",
        data: { email, token },
      });
      return result;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);

// Async thunk for validating user token
export const validateUserToken = createAsyncThunk(
  "login/validateUserToken",
  async (_, { rejectWithValue }) => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "null");
      if (!user || !user.token) {
        throw new Error("No token found");
      }
      const updatedUser = await apiPost({
        endpoint: "/validate-token",
        data: { token: user.token },
      });
      localStorage.setItem("user", JSON.stringify(updatedUser));
      return updatedUser;
    } catch (error: any) {
      const message = error?.message || "";
      // Only clear user on definitive auth failures, not on transient network errors
      const isAuthError =
        message.toLowerCase().includes("token") ||
        message.toLowerCase().includes("unauthorized") ||
        message.toLowerCase().includes("no token found");
      if (isAuthError) {
        localStorage.removeItem("user");
      }
      console.error("validateUserToken error", message);
      return rejectWithValue(message);
    }
  },
);

// Async thunk for logout
export const logout = createAsyncThunk("login/logout", async () => {
  localStorage.removeItem("user");
  // window.location.href = "/login";
});

// Slice
const loginSlice = createSlice({
  name: "login",
  initialState,
  reducers: {
    resetState: () => {
      return initialState;
    },
    updateUserData: (state, action) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
        // Update localStorage as well
        localStorage.setItem("user", JSON.stringify(state.user));
      }
    },
    // An admin-set password has been replaced by one of the user's own, so the
    // prompt is settled — in the session and in what is stored.
    passwordChanged: (state) => {
      if (state.user) {
        state.user = { ...state.user, mustChangePassword: false };
        localStorage.setItem("user", JSON.stringify(state.user));
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginWithEmail.pending, (state) => {
        state.loading = true;
        state.loginError = null;
      })
      .addCase(loginWithEmail.fulfilled, (state: any, action: PayloadAction<any>) => {
        state.loading = false;
        state.userIsValid = true;
        state.needValidation = false;
        state.loginSuccess = true;
        state.user = action.payload;
      })
      .addCase(loginWithEmail.rejected, (state: any, action: PayloadAction<any>) => {
        state.loading = false;
        state.userIsValid = false;
        // state.needValidation = false;
        state.loginError = action.payload;
        state.loginSuccess = false;
      })
      .addCase(loginWithMagicLink.pending, (state) => {
        state.loading = true;
        state.magicLinkSent = false;
        state.magicLinkError = null;
      })
      .addCase(loginWithMagicLink.fulfilled, (state) => {
        state.loading = false;
        state.magicLinkSent = true;
      })
      .addCase(loginWithMagicLink.rejected, (state: any, action: PayloadAction<any>) => {
        state.loading = false;
        state.magicLinkSent = false;
        state.magicLinkError = action.payload;
      })
      .addCase(verifyMagicLink.pending, (state) => {
        state.loading = true;
        state.verifyMagicLinkError = null;
        state.verifyMagicLinkSuccess = false;
      })
      .addCase(verifyMagicLink.fulfilled, (state: any, action: PayloadAction<any>) => {
        state.loading = false;
        state.userIsValid = true;
        state.needValidation = false;
        state.verifyMagicLinkError = null;
        state.verifyMagicLinkSuccess = true;
        state.user = action.payload;
      })
      .addCase(verifyMagicLink.rejected, (state: any, action: PayloadAction<any>) => {
        state.loading = false;
        state.userIsValid = false;
        state.verifyMagicLinkError = action.payload;
        state.verifyMagicLinkSuccess = false;
      })
      .addCase(requestResetToken.pending, (state) => {
        state.loading = true;
        state.resetTokenSuccess = false;
        state.resetTokenError = null;
      })
      .addCase(requestResetToken.fulfilled, (state) => {
        state.loading = false;
        state.resetTokenSuccess = true;
        state.resetTokenError = null;
      })
      .addCase(requestResetToken.rejected, (state: any, action: PayloadAction<any>) => {
        state.loading = false;
        state.resetTokenSuccess = false;
        state.resetTokenError = action.payload;
      })
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.resetPasswordSuccess = false;
        state.resetPasswordError = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.loading = false;
        state.resetPasswordSuccess = true;
        state.resetPasswordError = null;
      })
      .addCase(resetPassword.rejected, (state: any, action: PayloadAction<any>) => {
        state.loading = false;
        state.resetPasswordSuccess = false;
        state.resetPasswordError = action.payload;
      })
      .addCase(changePassword.pending, (state) => {
        state.loading = true;
        state.changePasswordSuccess = false;
        state.changePasswordError = null;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.loading = false;
        state.changePasswordSuccess = true;
        state.changePasswordError = null;
      })
      .addCase(changePassword.rejected, (state: any, action: PayloadAction<any>) => {
        state.loading = false;
        state.changePasswordSuccess = false;
        state.changePasswordError = action.payload;
      })
      .addCase(setPassword.pending, (state) => {
        state.loading = true;
        state.setPasswordSuccess = false;
        state.setPasswordError = null;
      })
      .addCase(setPassword.fulfilled, (state) => {
        state.loading = false;
        state.setPasswordSuccess = true;
        state.setPasswordError = null;
      })
      .addCase(setPassword.rejected, (state: any, action: PayloadAction<any>) => {
        state.loading = false;
        state.setPasswordSuccess = false;
        state.setPasswordError = action.payload;
      })
      .addCase(confirmEmail.pending, (state) => {
        state.loading = true;
        state.confirmEmailError = null;
        state.confirmEmailSuccess = false;
      })
      .addCase(confirmEmail.fulfilled, (state) => {
        state.loading = false;
        state.confirmEmailSuccess = true;
        state.confirmEmailError = null;
      })
      .addCase(confirmEmail.rejected, (state: any, action: PayloadAction<any>) => {
        state.loading = false;
        state.confirmEmailSuccess = false;
        state.confirmEmailError = action.payload;
      })
      .addCase(signup.pending, (state) => {
        state.loading = true;
        state.signUpError = null;
      })
      .addCase(signup.fulfilled, (state) => {
        state.loading = false;
        state.signUpError = null;
        state.signUpSuccess = true;
      })
      .addCase(signup.rejected, (state: any, action: PayloadAction<any>) => {
        state.loading = false;
        state.signUpError = action.payload;
        state.signUpSuccess = false;
      })
      .addCase(validateUserToken.pending, (state) => {
        state.validatingUser = true;
      })
      .addCase(validateUserToken.fulfilled, (state: any, action: PayloadAction<any>) => {
        state.validatingUser = false;
        state.userIsValid = true;
        state.needValidation = false;
        state.user = action.payload;
      })
      .addCase(validateUserToken.rejected, (state) => {
        state.validatingUser = false;
        state.needValidation = false;
        state.userIsValid = false;
        state.user = null;
        // state.loginError = action.payload;
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.userIsValid = false;
        state.loginError = null;
        state.loginSuccess = false;
      });
  },
});

export const { resetState, updateUserData, passwordChanged } = loginSlice.actions;

export default loginSlice.reducer;
